import { EnvironmentInjector, Injectable, runInInjectionContext } from '@angular/core';
import {
  AngularFirestore,
  AngularFirestoreCollection,
  AngularFirestoreCollectionGroup,
  AngularFirestoreDocument,
  DocumentData,
  QueryFn,
  QueryGroupFn
} from '@angular/fire/compat/firestore';

/**
 * Injection-context-safe facade over the AngularFirestore compat API.
 *
 * Since @angular/fire 19 the compat wrappers (AngularFirestoreCollection, AngularFirestoreDocument,
 * AngularFirestoreCollectionGroup) call inject() while they are being constructed, so calling
 * afs.collection() / afs.doc() outside an injection context (service methods, async callbacks,
 * RxJS operators...) throws NG0203. Every access here runs inside runInInjectionContext with the
 * root injector captured through DI, so callers do not need to care about it.
 *
 * Use full paths ("rooms/{roomId}/messages") instead of chaining .doc().collection(): each link of
 * a chain creates a new wrapper, and that would happen outside the context again.
 */
@Injectable({ providedIn: 'root' })
export class FirestoreService {
  constructor(private afs: AngularFirestore, private injector: EnvironmentInjector) {}

  public collection<T = DocumentData>(path: string, queryFn?: QueryFn): AngularFirestoreCollection<T> {
    return this.run(() => this.afs.collection<T>(path, queryFn));
  }

  public doc<T = DocumentData>(path: string): AngularFirestoreDocument<T> {
    return this.run(() => this.afs.doc<T>(path));
  }

  public collectionGroup<T = DocumentData>(collectionId: string, queryGroupFn?: QueryGroupFn<T>): AngularFirestoreCollectionGroup<T> {
    return this.run(() => this.afs.collectionGroup<T>(collectionId, queryGroupFn));
  }

  /** Runs any other compat call that is not covered by the methods above inside the injection context. */
  public run<R>(fn: () => R): R {
    return runInInjectionContext(this.injector, fn);
  }
}
